import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonIcon,
  IonText
} from '@ionic/react';
import { add, removeOutline } from 'ionicons/icons';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Badge,
  Tabs,
  Tab,
  Grid,
  InputAdornment,
  Chip
} from '@mui/material';
import { Search, ShoppingCart, ClearAll, Receipt } from '@mui/icons-material';

interface Product {
  id: string;
  name: string;
  price: number;
  img_src?: string;
}

interface OrderItem {
  product_id: string;
  product_name: string;
  quantity: number;
  price: number;
  image?: string;
}

interface Order {
  id: string;
  orderNumber?: string;
  customerName?: string;
  total: number;
  status?: string;
  items: OrderItem[];
}

interface POSDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  onSave: (updatedOrder: Order) => void;
//   categories:,
//   products.
}

const POSDialog: React.FC<POSDialogProps> = ({ open, onClose, order, onSave }) => {
  // Sample product data
  const products: Product[] = [
    { id: '101', name: 'Pizza', price: 12.99, img_src: 'https://via.placeholder.com/80' },
    { id: '102', name: 'Burger', price: 8.99, img_src: 'https://via.placeholder.com/80' },
    { id: '103', name: 'Soda', price: 2.99, img_src: 'https://via.placeholder.com/80' },
    { id: '104', name: 'Fries', price: 3.99, img_src: 'https://via.placeholder.com/80' },
    { id: '105', name: 'Salad', price: 7.99, img_src: 'https://via.placeholder.com/80' },
  ];

  const categories = [
    { category_id: '1', name: 'Food' },
    { category_id: '2', name: 'Drinks' },
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [items, setItems] = useState<OrderItem[]>(order.items);
  const [customerName, setCustomerName] = useState(order.customerName);

  // Calculate total amount
  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || product.id.startsWith(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  // Handle adding to cart
  const handleAddToCart = (product: Product) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.product_id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [
          ...prevItems,
          {
            product_id: product.id,
            product_name: product.name,
            price: product.price,
            quantity: 1,
            image: product.img_src
          }
        ];
      }
    });
  };

  // Handle removing from cart
  const cartItemRemoveHandler = (productId: string) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.product_id === productId);
      if (existingItem && existingItem.quantity > 1) {
        return prevItems.map(item =>
          item.product_id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      } else {
        return prevItems.filter(item => item.product_id !== productId);
      }
    });
  };

  // Handle clearing cart
  const clearCart = () => {
    setItems([]);
  };

  // Handle saving the order
  const handleSaveOrder = () => {
    const updatedOrder: Order = {
      ...order,
      customerName,
      items,
      total: totalAmount
    };
    onSave(updatedOrder);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen
      sx={{ '& .MuiDialog-paper': { height: '90%',width:'80%' } }}
    >
      <DialogTitle>
        Editing Order: {order.orderNumber}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: 'calc(100% - 64px)' }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Customer Info */}
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
            <TextField
              fullWidth
              label="Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              variant="outlined"
            />
          </Box>

          {/* Main POS Content */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Product Selection Panel */}
            <Box sx={{ width: '70%', p: 2, overflow: 'auto' }}>
              {/* Search and Categories */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search products..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Tabs
                value={selectedCategory}
                onChange={(e, newValue) => setSelectedCategory(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2 }}
              >
                <Tab label="All" value={null} />
                {categories.map((category, i) => (
                  <Tab key={i} label={category.name} value={category.category_id} />
                ))}
              </Tabs>

              {/* Product Grid */}
              <Grid container spacing={2}>
                {filteredProducts.map((product, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Box
                      onClick={() => handleAddToCart(product)}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Avatar
                          src={product.img_src}
                          alt={product.name}
                          sx={{ width: 80, height: 80, mb: 1 }}
                        />
                        <Typography variant="subtitle1" align="center">
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${product.price.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Cart Panel */}
            <Box sx={{
              width: '30%',
              borderLeft: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6">
                  <Badge badgeContent={items.length} color="primary" sx={{ mr: 1 }}>
                    <ShoppingCart />
                  </Badge>
                  Current Order
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<ClearAll />}
                  onClick={clearCart}
                  disabled={items.length === 0}
                >
                  Clear
                </Button>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {items.length === 0 ? (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    textAlign: 'center'
                  }}>
                    <ShoppingCart sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      Your cart is empty
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select products to add to order
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {items.map((item, i) => (
                      <ListItem key={i} sx={{ py: 1 }}>
                        <Avatar
                          src={item?.image}
                          alt={item.product_name}
                          sx={{ width: 40, height: 40, mr: 2 }}
                        />
                        <ListItemText
                          primary={item.product_name}
                        //   secondary={`$${item?.price?.toFixed(2)} each`}
                        />
                        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center' }}>
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => cartItemRemoveHandler(item.product_id)}
                          >
                            <IonIcon slot="icon-only" icon={removeOutline} />
                          </IonButton>

                          <IonText>{item.quantity}</IonText>

                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={() => handleAddToCart({
                              id: item.product_id,
                              name: item.product_name,
                              price: item.price,
                              img_src: item.image
                            })}
                          >
                            <IonIcon slot="icon-only" icon={add} />
                          </IonButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {/* Order Summary */}
              <Box sx={{
                p: 2,
                borderTop: '1px solid #e0e0e0',
                bgcolor: 'background.paper'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1">Total:</Typography>
                  <Typography variant="subtitle1">
                    ${totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSaveOrder}
          startIcon={<Receipt />}
          disabled={items.length === 0}
        >
          Update Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default POSDialog;